import {ApolloProvider, useQuery} from '@apollo/client/react';
import type {ShellAccount} from '@wandb/forge-shell-contract';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useSearchParams} from 'react-router-dom';

import {
  ACCOUNT_SELECTOR_VALUE,
  accountFromUrlBroadcast,
  buildAccountList,
  getAccountFromStorage,
  getDefaultAccount,
  hasPersonalEntity,
  LAST_ACCOUNT_SELECTOR_VALUE,
  type Account,
  type ShellViewer,
  writeAccountToStorage,
} from './accountModel';
import {
  getMockUser,
  hasMockOverride,
  mockAccountList,
  mockViewer,
} from './accountMocks';
import {
  ShellAccountDataDocument,
  ShellPersonalEntityDocument,
} from '../generated/graphql';
import type {
  ShellAccountDataQuery,
  ShellPersonalEntityQuery,
} from '../generated/graphql';
import {shellApolloClient} from '../apolloClient';
import {useBroadcastUrlAccount} from './UrlAccountBroadcastContext';

type AccountContextValue = {
  viewer: ShellViewer | null;
  accountList: Account[];
  selectedAccount: Account | null;
  isLoading: boolean;
  isMock: boolean;
  error: Error | undefined;
  selectAccount: (account: Account) => void;
  selectShellAccount: (account: ShellAccount) => void;
};

const AccountContext = createContext<AccountContextValue | undefined>(
  undefined
);

function parseUserInfo(value: unknown): {isPersonalEntityHidden?: boolean} {
  let parsed = value;
  if (typeof value === 'string') {
    try {
      parsed = JSON.parse(value) as unknown;
    } catch {
      return {};
    }
  }
  if (typeof parsed !== 'object' || parsed == null) {
    return {};
  }
  const hidden = (parsed as {isPersonalEntityHidden?: unknown})
    .isPersonalEntityHidden;
  return typeof hidden === 'boolean' ? {isPersonalEntityHidden: hidden} : {};
}

function AccountProviderContent({children}: {children: React.ReactNode}) {
  const [searchParams] = useSearchParams();
  const viewerOverride = searchParams.get('viewer');
  const hasGlobalOverride = Object.prototype.hasOwnProperty.call(
    window,
    '__FORGE_SHELL_MOCK_USER__'
  );
  const mockOverride =
    import.meta.env.DEV && hasMockOverride(viewerOverride, hasGlobalOverride);
  const mockUser = mockOverride
    ? getMockUser(viewerOverride, hasGlobalOverride)
    : null;
  const accountQuery = useQuery<ShellAccountDataQuery>(
    ShellAccountDataDocument,
    {skip: mockOverride}
  );
  const fetchedUsername = accountQuery.data?.viewer?.username ?? null;
  const personalEntityQuery = useQuery<ShellPersonalEntityQuery>(
    ShellPersonalEntityDocument,
    {
      skip: mockOverride || fetchedUsername == null,
      variables: {entityName: fetchedUsername ?? ''},
    }
  );

  let viewer: ShellViewer | null;
  if (mockOverride) {
    if (mockUser == null) {
      viewer = null;
    } else {
      viewer = mockViewer(mockUser);
    }
  } else {
    viewer = accountQuery.data?.viewer ?? null;
  }

  const queriedViewer = accountQuery.data?.viewer ?? null;
  const personalEntity = personalEntityQuery.data?.entity ?? null;
  const accountList = useMemo((): Account[] => {
    if (mockOverride) {
      return mockUser == null ? [] : mockAccountList(mockUser);
    }
    if (viewer == null || queriedViewer == null) {
      return [];
    }
    return buildAccountList(
      queriedViewer.organizations,
      viewer,
      parseUserInfo(queriedViewer.userInfo).isPersonalEntityHidden ?? false,
      hasPersonalEntity(personalEntity)
    );
  }, [mockOverride, mockUser, viewer, queriedViewer, personalEntity]);

  const [accountSelection, setAccountSelection] = useState<{
    viewerId: string | null;
    account: Account | null;
  } | null>(null);
  const isLoading =
    !mockOverride &&
    (accountQuery.loading ||
      (fetchedUsername != null && personalEntityQuery.loading));

  const urlAccountBroadcast = useBroadcastUrlAccount();
  // The remote names the account its URL resolves to; the shell maps it to a
  // member Account, or a dummy stand-in it never stores.
  let urlAccount: Account | undefined;
  if (!isLoading && viewer != null && urlAccountBroadcast != null) {
    urlAccount = accountFromUrlBroadcast(
      urlAccountBroadcast,
      viewer,
      accountList
    );
  }

  let defaultSelectedAccount: Account | null = null;
  if (!isLoading && viewer != null) {
    const sessionAccount = getAccountFromStorage(
      'sessionStorage',
      ACCOUNT_SELECTOR_VALUE,
      accountList
    );
    const localAccount = getAccountFromStorage(
      'localStorage',
      LAST_ACCOUNT_SELECTOR_VALUE,
      accountList
    );
    defaultSelectedAccount =
      sessionAccount ?? localAccount ?? getDefaultAccount(viewer, accountList);
  }
  const viewerId = viewer?.id ?? null;
  const chosenAccount =
    accountSelection?.viewerId === viewerId
      ? accountSelection.account
      : defaultSelectedAccount;
  // Broadcast URL account > header pick > storage > default.
  const selectedAccount = urlAccount ?? chosenAccount;

  const selectAccount = useCallback(
    (account: Account) => {
      setAccountSelection({viewerId, account});
      writeAccountToStorage(account);
    },
    [viewerId]
  );

  // A remote's pick, e.g. after creating an organization and redirecting to a
  // page whose URL names none. Only member accounts resolve; dummies and
  // unmatched broadcasts are ignored, and a URL broadcast still outranks the pick.
  const refetchAccounts = accountQuery.refetch;
  const selectShellAccount = useCallback(
    (shellAccount: ShellAccount) => {
      if (viewer == null || shellAccount.isDummyAccount) {
        return;
      }
      const resolved = accountFromUrlBroadcast(
        shellAccount,
        viewer,
        accountList
      );
      if (resolved != null && !resolved.isDummyAccount) {
        selectAccount(resolved);
        return;
      }
      if (mockOverride) {
        return;
      }
      // The remote may name an account created moments ago (CreateTeamDrawer),
      // before the account query knows it — refetch and try once more.
      void refetchAccounts().then(result => {
        const freshViewer = result.data?.viewer;
        if (freshViewer == null || freshViewer.id !== viewer.id) {
          return;
        }
        const freshList = buildAccountList(
          freshViewer.organizations,
          freshViewer,
          parseUserInfo(freshViewer.userInfo).isPersonalEntityHidden ?? false,
          hasPersonalEntity(personalEntity)
        );
        const fresh = accountFromUrlBroadcast(
          shellAccount,
          freshViewer,
          freshList
        );
        if (fresh != null && !fresh.isDummyAccount) {
          selectAccount(fresh);
        }
      });
    },
    [
      viewer,
      accountList,
      mockOverride,
      refetchAccounts,
      personalEntity,
      selectAccount,
    ]
  );

  const urlMemberAccount =
    urlAccount != null && !urlAccount.isDummyAccount ? urlAccount : null;
  // Render-time derived state: seeds the fallback for when the remote's URL
  // stops naming an account. Settles after one pass because the guard
  // compares ids.
  if (
    urlMemberAccount != null &&
    (accountSelection?.viewerId !== viewerId ||
      accountSelection.account?.id !== urlMemberAccount.id)
  ) {
    setAccountSelection({viewerId, account: urlMemberAccount});
  }
  useEffect(() => {
    if (urlMemberAccount != null) {
      writeAccountToStorage(urlMemberAccount);
    }
  }, [urlMemberAccount]);

  // Dummy accounts are listed only while the URL names them and never stored.
  const effectiveAccountList =
    selectedAccount?.isDummyAccount &&
    !accountList.some(account => account.id === selectedAccount.id)
      ? [...accountList, selectedAccount]
      : accountList;

  const value = {
    viewer,
    accountList: effectiveAccountList,
    selectedAccount,
    isLoading,
    isMock: mockOverride,
    error: mockOverride ? undefined : accountQuery.error,
    selectAccount,
    selectShellAccount,
  };

  return (
    <AccountContext.Provider value={value}>{children}</AccountContext.Provider>
  );
}

export const AccountProvider = ({children}: {children: React.ReactNode}) => (
  <ApolloProvider client={shellApolloClient()}>
    <AccountProviderContent>{children}</AccountProviderContent>
  </ApolloProvider>
);

export function useAccountContext(): AccountContextValue {
  const context = useContext(AccountContext);
  if (context == null) {
    throw new Error('useAccountContext must be used inside AccountProvider');
  }
  return context;
}
